// client/src/coms/components/ComsLayout.js
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ServerSidebar from './ServerSidebar';
import ServerRail from './ServerRail';
import SocialSidebar from './SocialSidebar';
import TopTabs from './TopTabs';
import MembersPanel from './MembersPanel';
import ComsMobileShell from './ComsMobileShell';

import ComsCornerAd from './ComsCornerAd';
import CreateServerModal from './CreateServerModal';
import CreateChannelModal from './CreateChannelModal';
import { comsApiGet, comsApiPost, comsApiPatch } from '../comsApi';

import TabRouter from './comslayout_parts/TabRouter';
import GamesView from '../views/GamesView';
import { clearPendingGameCode, getActiveGame, peekPendingGameCode, subscribeActiveGame } from '../views/games_parts/activeGameStore';
import useVoiceSession from './comslayout_parts/useVoiceSession';
import useServersState from './comslayout_parts/useServersState';
import useComsViewport from './comslayout_parts/useComsViewport';
import useSidebarResize from './comslayout_parts/useSidebarResize';
import useActiveLocation from './comslayout_parts/useActiveLocation';
import useJoinServer from './comslayout_parts/useJoinServer';
import ComsEmptyState from './comslayout_parts/ComsEmptyState';
import { isComsAdsEnabled, isMobileViewport } from './comslayout_parts/layoutUtils';
import MobileNavBar from './mobile/MobileNavBar';
import MobileVoiceDock, { voiceDockState } from './mobile/MobileVoiceDock';
import MobileMoreSheet from './mobile/MobileMoreSheet';
import MobileHeader, { useServerLogo } from './mobile/MobileHeader';
import NavIcon from './mobile/NavIcon';
import MobileNavMenu, { MobileMenuLauncher } from './mobile/MobileNavMenu';
import useAutoHideNav from './mobile/useAutoHideNav';
import { barItems, layoutParts } from '../personalisation/mobileLayout';
import ChatsView from '../conversations/ChatsView';
import MobileDmsView, { DmsSections } from '../conversations/MobileDmsView';
import QuickSwitcher from '../conversations/QuickSwitcher';
import useConversationIndex, { channelDbId as convChannelDbId, serverKeyOf as convServerKeyOf } from '../conversations/useConversationIndex';
import { recordRecent } from '../conversations/recentConversations';
import { openTarget as openConversationTarget, openArea as openOrgArea } from '../organisation/orgShared';
import { getPersistedComsNav, setPersistedComsNav } from './comslayout_parts/comsSession';
import useServerReadSummary from './comslayout_parts/useServerReadSummary';
import useServerNavMode, { SERVER_NAV_SIDEBAR } from './comslayout_parts/serverNavPrefs';
import { usePersonalisation } from '../personalisation/PersonalisationProvider';
import { LayoutEditBar, RegionTag, ServerThemePrompt } from '../personalisation/LayoutEditing';
import { ToastHost, notify } from '../organisation/orgShared';
import ReportHost from '../serveradmin/ReportHost';
import InteractionHost from '../interactions/InteractionHost';
import useWorkspace from '../workspace/useWorkspace';
import ConversationTabStrip from '../workspace/ConversationTabStrip';
import SplitPane from '../workspace/SplitPane';
import usePanelResize from '../workspace/usePanelResize';
import { ServerHeaderBanner } from './ServerBanner';
import useWorkspaceShortcuts from '../workspace/useWorkspaceShortcuts';
import { channelTab, dmTab, tabIdOf } from '../workspace/workspaceModel';
import { ENGAGED_EVENT } from '../workspace/engagement';
import { readTabMemory, writeTabMemory } from '../workspace/tabMemory';

// Areas whose engines need exact pointer coordinates: interface scaling is not applied over them.
const UNSCALED_TABS = new Set(['canvas', 'games', 'studio', 'maps']);
// Areas that count as "an activity" for the chat-beside-activity placement.
const ACTIVITY_TABS = new Set(['games', 'canvas', 'watch']);

function useWindowSize() {
  const [size, setSize] = useState(() => ({ w: typeof window !== 'undefined' ? window.innerWidth : 1280, h: typeof window !== 'undefined' ? window.innerHeight : 800 }));
  useEffect(() => {
    let raf = 0;
    const on = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setSize({ w: window.innerWidth, h: window.innerHeight });
      });
    };
    window.addEventListener('resize', on);
    return () => {
      window.removeEventListener('resize', on);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return size;
}

const LAST_COMMS_SERVER_KEY = 'lastCommsServerId';
const HEADER_BAR_STATE_KEY = 'vox:headerBarState';

function extractServerKey(s) {
  if (!s || typeof s !== 'object') return '';
  if (s.id != null) return String(s.id).trim();
  if (s.server_id != null) return String(s.server_id).trim();
  if (s.public_id != null) return String(s.public_id).trim();
  if (s.server_key != null) return String(s.server_key).trim();
  return '';
}

// Invitation links open the shell at /comms?gameCode=CODE (read when the page loaded, or still in the URL).
function readGameCodeFromUrl() {
  try {
    return peekPendingGameCode() || String(new URLSearchParams(window.location.search).get('gameCode') || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
  } catch {
    return '';
  }
}

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

function toInt01(v) {
  if (v === true) return 1;
  if (v === false) return 0;
  const n = Number(v);
  return n === 1 ? 1 : 0;
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function extractChannelDbId(channel) {
  const c = channel && typeof channel === 'object' ? channel : null;
  if (!c) return 0;

  const raw =
    c.db_id != null
      ? c.db_id
      : c.channel_db_id != null
        ? c.channel_db_id
        : c.text_channel_id != null
          ? c.text_channel_id
          : c.channel_id != null
            ? c.channel_id
            : c.id != null
              ? c.id
              : 0;

  return toInt(raw, 0);
}

function getChannelKind(channel) {
  const c = channel && typeof channel === 'object' ? channel : null;
  return String((c && c.kind) || '').trim().toLowerCase();
}

function channelCanHavePinnedMessages(channel) {
  const kind = getChannelKind(channel);
  if (kind === 'voice') return false;
  return extractChannelDbId(channel) > 0;
}

function normalizePinnedMessage(msg) {
  const m = msg && typeof msg === 'object' ? msg : {};
  return {
    ...m,
    id: toInt(m.id, 0),
    user_id: toInt(m.user_id, 0),
    is_pinned: toInt01(m.is_pinned),
    is_motd: toInt01(m.is_motd),
    is_super_pinned: toInt01(m.is_super_pinned),
    is_header_message: toInt01(m.is_header_message),
    body: String(m.body || m.text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n'),
    text: String(m.text || m.body || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n'),
    author: String(m.author || m.username || m.display_name || '').trim(),
    stamp: String(m.stamp || '').trim(),
  };
}

function buildHeaderPreview(msg, maxLen = 220) {
  const m = normalizePinnedMessage(msg);
  const raw = String(m.text || m.body || '').replace(/\s+/g, ' ').trim();
  if (!raw) return '(empty)';
  if (raw.length <= maxLen) return raw;
  return `${raw.slice(0, maxLen - 1).trim()}…`;
}

function readHeaderBarStateMap() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    const raw = window.localStorage.getItem(HEADER_BAR_STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeHeaderBarState(channelDbId, messageId, open) {
  const cid = toInt(channelDbId, 0);
  const mid = toInt(messageId, 0);
  if (!cid || !mid) return;

  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const map = readHeaderBarStateMap();
    map[String(cid)] = {
      messageId: mid,
      open: !!open,
    };
    window.localStorage.setItem(HEADER_BAR_STATE_KEY, JSON.stringify(map));
  } catch {}
}

function readHeaderBarOpen(channelDbId, messageId) {
  const cid = toInt(channelDbId, 0);
  const mid = toInt(messageId, 0);
  if (!cid || !mid) return true;

  try {
    const map = readHeaderBarStateMap();
    const entry = map[String(cid)];
    if (!entry || typeof entry !== 'object') return true;
    const savedMessageId = toInt(entry.messageId, 0);
    if (savedMessageId !== mid) return true;
    return entry.open !== false;
  } catch {
    return true;
  }
}

export default function ComsLayout({ user, setUser }) {
  const navigate = useNavigate();
  const params = useParams();

  const {
    servers,
    serversLoaded,
    apiAvailable,
    authState,
    lastApiError,
    refreshServers,
  } = useServersState();

  const { mobile, leftOpen, setLeftOpen, rightOpen, setRightOpen, closePanelsRef } = useComsViewport();
  // The old local-only resize hook is kept for its stored width (legacy seed); widths now come from layout prefs.
  useSidebarResize();
  const pz = usePersonalisation();
  const layout = pz.layout;
  const windowSize = useWindowSize();

  const {
    server,
    activeServerId,
    setActiveServerId,
    activeChannelId,
    activeChannel,
    activeChannelReady,
    onSelectServer,
    onSelectChannel,
    goTo,
  } = useActiveLocation({ params, navigate, servers, serversLoaded });
  const serverLogo = useServerLogo(server);

  useEffect(() => {
    pz.setActiveServerKey(String(activeServerId || '').trim());
    // setActiveServerKey is a state setter (stable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeServerId]);

  const workspace = useWorkspace({ user, servers, serversLoaded, restore: pz.globalPrefs.tabs.restore });

  const [pendingGameCode, setPendingGameCode] = useState(readGameCodeFromUrl);
  const [activeTab, setActiveTab] = useState(() => (pendingGameCode ? 'games' : getPersistedComsNav().activeTab || 'coms'));
  // Games open from the server sidebar inside this shell. While the player is in a game the Games view
  // stays mounted (hidden) in other areas, so its live connection, presence and board survive tab changes.
  const [activeGame, setActiveGameState] = useState(getActiveGame);
  useEffect(() => subscribeActiveGame(setActiveGameState), []);
  useEffect(() => {
    if (!pendingGameCode) return;
    clearPendingGameCode();
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('gameCode');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {}
  }, [pendingGameCode]);
  const gamesMounted = activeTab === 'games' || Boolean(activeGame);
  const keepTabOnServerSwitch = (tab) => (tab === 'games' ? 'games' : 'coms');

  // vox-open-settings { tab } switches to Settings (SettingsView picks the tab).
  useEffect(() => {
    const onOpen = () => setActiveTab('settings');
    window.addEventListener('vox-open-settings', onOpen);
    return () => window.removeEventListener('vox-open-settings', onOpen);
  }, []);
  // vox-open-server-admin { section } opens Server Settings at a section; vox-open-server-events
  // opens the server's events.
  useEffect(() => {
    const onAdmin = (e) => {
      const section = e && e.detail && e.detail.section;
      if (section) window.__voxPendingAdminSection = section;
      setActiveTab('server-settings');
    };
    const onEvents = () => setActiveTab('server-events');
    window.addEventListener('vox-open-server-admin', onAdmin);
    window.addEventListener('vox-open-server-events', onEvents);
    return () => {
      window.removeEventListener('vox-open-server-admin', onAdmin);
      window.removeEventListener('vox-open-server-events', onEvents);
    };
  }, []);
  // Watch Together used to be its own tab; old saved tabs and links open it in Activities instead.
  useEffect(() => {
    if (activeTab !== 'watch') return;
    const detail = { category: 'Watch' };
    window.__voxPendingGamesContext = detail;
    setActiveTab('games');
    window.dispatchEvent(new CustomEvent('vox-open-games', { detail }));
  }, [activeTab]);
  // vox-open-games { category } opens the Activities destination (the Activities view picks the category).
  useEffect(() => {
    const onOpen = () => setActiveTab('games');
    window.addEventListener('vox-open-games', onOpen);
    return () => window.removeEventListener('vox-open-games', onOpen);
  }, []);
  // vox-open-marketplace (Settings → Stickers → Browse Marketplace) opens the Marketplace tab.
  useEffect(() => {
    const onOpen = () => setActiveTab('marketplace');
    window.addEventListener('vox-open-marketplace', onOpen);
    return () => window.removeEventListener('vox-open-marketplace', onOpen);
  }, []);
  // Organisation areas: 'library' (Files & resources), 'search', 'bookmarks'. vox-open-org { area, ...context }.
  const closeDrawersOnPhone = useCallback(() => {
    try {
      if (isMobileViewport() && closePanelsRef.current) closePanelsRef.current();
    } catch {}
  }, [closePanelsRef]);
  const [orgContext, setOrgContext] = useState(null);
  useEffect(() => {
    const onOpen = (e) => {
      const d = (e && e.detail) || {};
      if (!['library', 'search', 'bookmarks'].includes(d.area)) return;
      setOrgContext({ ...d, at: Date.now() });
      setActiveTab(d.area);
      closeDrawersOnPhone();
    };
    window.addEventListener('vox-open-org', onOpen);
    // Ctrl/Cmd+K (or Ctrl/Cmd+Shift+F) opens search from anywhere.
    const onKey = (e) => {
      const k = String(e.key || '').toLowerCase();
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      if ((k === 'k' && !e.shiftKey) || (k === 'f' && e.shiftKey)) {
        e.preventDefault();
        setOrgContext({ area: 'search', at: Date.now() });
        setActiveTab('search');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('vox-open-org', onOpen);
      window.removeEventListener('keydown', onKey);
    };
  }, [closeDrawersOnPhone]);
  const [messageSearch, setMessageSearchState] = useState('');
  const [rightPanelTab, setRightPanelTab] = useState('members');
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [headerMessage, setHeaderMessage] = useState(null);
  const [headerBarOpen, setHeaderBarOpen] = useState(true);
  const [createServerOpen, setCreateServerOpen] = useState(false);
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [comsAdsEnabled] = useState(() => isComsAdsEnabled());
  const [socialSection, setSocialSection] = useState(() => getPersistedComsNav().socialSection || 'friends');
  const [selectedSocialThreadId, setSelectedSocialThreadId] = useState(() => getPersistedComsNav().selectedSocialThreadId || '');

  useEffect(() => {
    setPersistedComsNav({
      activeTab,
      socialSection,
      selectedSocialThreadId,
    });
  }, [activeTab, socialSection, selectedSocialThreadId]);

  useEffect(() => {
    const current = String(activeServerId || '').trim();
    if (!current) return;
    try {
      localStorage.setItem(LAST_COMMS_SERVER_KEY, current);
    } catch {}
  }, [activeServerId]);

  useEffect(() => {
    if (!serversLoaded) return;
    if (!Array.isArray(servers) || !servers.length) return;

    const paramServerId = String(
      params && params.serverId != null
        ? params.serverId
        : params && params.server_id != null
          ? params.server_id
          : ''
    ).trim();

    const activeId = String(activeServerId || '').trim();
    const hasValidActive = !!activeId && servers.some((s) => extractServerKey(s) === activeId);
    if (hasValidActive) return;

    const hasValidParam = !!paramServerId && servers.some((s) => extractServerKey(s) === paramServerId);
    if (hasValidParam) return;

    let saved = '';
    try {
      saved = String(localStorage.getItem(LAST_COMMS_SERVER_KEY) || '').trim();
    } catch {
      saved = '';
    }

    const savedExists = !!saved && servers.some((s) => extractServerKey(s) === saved);
    const fallback = extractServerKey(servers[0]);
    const nextId = savedExists ? saved : fallback;

    if (!nextId) return;
    if (nextId === activeId && nextId === paramServerId) return;

    setActiveServerId(nextId);
    navigate(`/comms/${encodeURIComponent(nextId)}`, { replace: true });
  }, [serversLoaded, servers, params, activeServerId, setActiveServerId, navigate]);

  const {
    voiceSession,
    voiceRoomObj,
    voiceShowVideo,
    voiceVideoMode,
    voiceVideoErr,
    joinVoiceChannel,
    leaveVoiceChannel,
    toggleVoiceVideo,
    toggleVoiceCamera,
    toggleVoiceScreen,
    onToggleVoiceMute,
    onToggleVoiceDeafen,
    chatNameById,
  } = useVoiceSession({
    servers,
    serversLoaded,
    apiAvailable,
    server,
    activeServerId,
    setActiveServerId,
    navigate,
  });

  const {
    joinInviteCode,
    setJoinInviteCode,
    joinBusy,
    joinErr,
    handleJoinServer,
    openJoinOnEnter,
  } = useJoinServer({
    servers,
    refreshServers,
    setActiveServerId,
    setActiveTab,
    navigate,
  });

  const onChannelsChanged = useCallback(async () => {
    await refreshServers(false);
  }, [refreshServers]);

  // ---------- conversation tabs ----------
  const myUid = user && user.id != null ? String(user.id) : '';
  const serverKeyNow = String(activeServerId || '').trim();
  // The conversation the main view shows right now ('' when it shows something else, e.g. Activities).
  const locationConvId =
    activeTab === 'coms' && server && activeChannelReady && activeChannelId && String((activeChannel && activeChannel.kind) || 'text') === 'text'
      ? tabIdOf(channelTab(serverKeyNow, activeChannelId))
      : activeTab === 'social' && socialSection === 'dms' && selectedSocialThreadId
        ? tabIdOf(dmTab(selectedSocialThreadId))
        : '';
  // ---------- moving between conversations (Chats, quick switcher, bottom bar) ----------
  // Every conversation shown is remembered (ids and times only) so Chats and the switcher can offer it again.
  useEffect(() => {
    if (locationConvId) recordRecent(myUid, locationConvId);
  }, [locationConvId, myUid]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // Phones without a bar (Dropdown, Gestures): the navigation menu.
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const mobileMainRef = useRef(null);
  const gestureHandlerRef = useRef(() => {});
  const navAutoHide = mobile && pz.mobilePrefs.navBehaviour === 'autohide' && ['bottom', 'top'].includes(pz.mobilePrefs.navPosition);
  const navHidden = useAutoHideNav(navAutoHide, mobileMainRef, `${activeTab}:${locationConvId}`);
  // Conversations visited in this session, for the "previous / next conversation" gestures (like Back and Forward).
  const convHistoryRef = useRef({ list: [], at: -1, jumping: '' });
  useEffect(() => {
    if (!locationConvId) return;
    const h = convHistoryRef.current;
    if (h.jumping === locationConvId) {
      h.jumping = '';
      return;
    }
    if (h.list[h.at] === locationConvId) return;
    h.list = [...h.list.slice(0, h.at + 1), locationConvId].slice(-30);
    h.at = h.list.length - 1;
  }, [locationConvId]);
  const stepConversation = useCallback((delta) => {
    const h = convHistoryRef.current;
    const to = h.at + delta;
    const id = h.list[to];
    if (!id) {
      notify(delta < 0 ? 'No earlier conversation' : 'No later conversation', { ms: 1500 });
      return;
    }
    h.at = to;
    h.jumping = id;
    const ch = /^c:([^:]+):text:(\d+)$/.exec(id);
    const dm = /^d:(\d+)$/.exec(id);
    if (ch) openConversationTarget({ kind: 'channel', server_key: ch[1], channel_kind: 'text', channel_id: Number(ch[2]) });
    else if (dm) openConversationTarget({ kind: 'dm', thread_id: Number(dm[1]) });
  }, []);
  const pinnedConversationIds = useMemo(() => new Set(workspace.items.filter((it) => it.pinned).map((it) => it.id)), [workspace.items]);
  const openConversation = useCallback((item) => {
    setSwitcherOpen(false);
    setMoreOpen(false);
    setNavMenuOpen(false);
    if (item && item.open) openConversationTarget(item.open);
  }, []);
  const pinConversation = useCallback(
    (item, pinned) => {
      if (!item) return;
      if (pinned && !workspace.items.some((it) => it.id === item.id)) {
        const tab = item.kind === 'dm' ? dmTab(String(item.open.thread_id)) : channelTab(item.serverKey, `text:${item.open.channel_id}`);
        workspace.open(tab, { newTab: true, activate: false });
      }
      workspace.pin(item.id, pinned);
    },
    [workspace]
  );
  // Ctrl/⌘+K opens the switcher anywhere (also on desktop).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSwitcherOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const voiceWhere = useMemo(() => {
    const sid = String((voiceSession && voiceSession.serverId) || '').trim();
    const cid = String((voiceSession && voiceSession.channelId) || '').trim();
    const s = sid ? (servers || []).find((x) => convServerKeyOf(x) === sid || String(x.id) === sid) : null;
    const chans = s && Array.isArray(s.channels) ? s.channels : [];
    const ch = chans.find((c) => String(c.id) === cid || (convChannelDbId(c.id) && String(convChannelDbId(c.id)) === cid) || (convChannelDbId(cid) && convChannelDbId(c.id) === convChannelDbId(cid) && String(c.kind || '') === 'voice'));
    return { serverKey: s ? convServerKeyOf(s) : sid, serverName: s ? String(s.name || '') : '', channelName: ch ? String(ch.name || '') : '', channelDbId: ch ? convChannelDbId(ch.id) : convChannelDbId(cid) || Number(cid) || 0 };
  }, [voiceSession, servers]);

  const locationConvRef = useRef(locationConvId);
  locationConvRef.current = locationConvId;
  const wsRef = useRef(workspace.ws);
  wsRef.current = workspace.ws;
  const followedRef = useRef('');
  const firstFollowRef = useRef(true);
  const openNextAsNewTabRef = useRef(false);
  // A conversation the user wrote in before its tab existed (e.g. the workspace was still loading): its tab
  // opens saved instead of as the preview.
  const engagedBeforeTabRef = useRef('');
  // Set by tab operations that change which conversation is active (close, reopen, swap...). Only those move
  // the main view; restoring the saved tabs on load never overrides the address the user opened.
  const pendingNavRef = useRef(false);
  const navigating = useCallback(
    (fn) =>
      (...args) => {
        pendingNavRef.current = true;
        return fn(...args);
      },
    []
  );

  const setMessageSearch = useCallback(
    (value) => {
      setMessageSearchState(value);
      if (locationConvRef.current) writeTabMemory(myUid, locationConvRef.current, { search: value });
    },
    [myUid]
  );

  // Each conversation keeps its own message search.
  useEffect(() => {
    const saved = locationConvId ? readTabMemory(myUid, locationConvId) : null;
    setMessageSearchState(saved && typeof saved.search === 'string' ? saved.search : '');
  }, [locationConvId, myUid]);

  // The main view moved to a conversation (sidebar, DM list, link): the tab bar follows.
  useEffect(() => {
    if (!workspace.loaded || !locationConvId) return;
    const ws = wsRef.current;
    if (ws.active === locationConvId) {
      followedRef.current = locationConvId;
      firstFollowRef.current = false;
      return;
    }
    const tab = locationConvId.startsWith('d:') ? dmTab(locationConvId.slice(2)) : channelTab(serverKeyNow, activeChannelId);
    followedRef.current = locationConvId;
    // Browsing opens the conversation in the preview tab (replacing the previous preview, never a saved tab);
    // Ctrl/middle-click asked for a new saved tab.
    const newTab = openNextAsNewTabRef.current || engagedBeforeTabRef.current === locationConvId;
    firstFollowRef.current = false;
    openNextAsNewTabRef.current = false;
    engagedBeforeTabRef.current = '';
    workspace.open(tab, { newTab });
    // Only location changes drive this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationConvId, workspace.loaded]);

  // Writing in a conversation keeps its tab (useWorkspace promotes the preview). Writing before the tab exists
  // (the workspace still loading) is remembered here, so the tab then opens saved instead of as the preview.
  useEffect(() => {
    const onEngaged = (e) => {
      const id = String((e && e.detail && e.detail.id) || '');
      if (!id || id !== locationConvRef.current) return;
      if (!wsRef.current.tabs.some((t) => tabIdOf(t) === id)) engagedBeforeTabRef.current = id;
    };
    window.addEventListener(ENGAGED_EVENT, onEngaged);
    return () => window.removeEventListener(ENGAGED_EVENT, onEngaged);
  }, []);

  const showConversation = useCallback(
    (id) => {
      const item = workspace.items.find((it) => it.id === id);
      if (!item) return;
      if (item.kind === 'channel') {
        setActiveTab('coms');
        goTo(item.tab.s, item.tab.c);
      } else {
        setActiveTab('social');
        setSocialSection('dms');
        setSelectedSocialThreadId(item.tab.t);
      }
    },
    [workspace.items, goTo]
  );

  // A tab operation (close, reopen, swap) made another conversation active: show it.
  useEffect(() => {
    const active = workspace.ws.active;
    const requested = pendingNavRef.current;
    pendingNavRef.current = false;
    if (!active || active === followedRef.current) return;
    followedRef.current = active;
    if (!requested || active === locationConvRef.current) return;
    if (activeTab === 'coms' || activeTab === 'social') showConversation(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace.ws.active]);

  const activateConversation = useCallback(
    (id) => {
      followedRef.current = id;
      workspace.activate(id);
      showConversation(id);
      if (mobile && closePanelsRef.current) closePanelsRef.current();
    },
    [workspace, showConversation, mobile, closePanelsRef]
  );

  // Ctrl/Cmd + click or middle click on a channel opens it in a new background tab.
  const onSidebarClickCapture = useCallback(
    (e) => {
      const isAux = e.type === 'auxclick' && e.button === 1;
      if (!isAux && !(e.type === 'click' && (e.ctrlKey || e.metaKey))) return;
      const el = e.target && e.target.closest ? e.target.closest('[data-channel-id]') : null;
      if (!el || !serverKeyNow) return;
      e.preventDefault();
      e.stopPropagation();
      workspace.open(channelTab(serverKeyNow, el.getAttribute('data-channel-id')), { newTab: true, activate: false });
    },
    [workspace, serverKeyNow]
  );

  const splitAvailable = !mobile && windowSize.w >= 1000 && (layout.split.mode !== 'down' || windowSize.h >= 640);
  const secondaryItem = useMemo(() => {
    const ws = workspace.ws;
    if (ws.split.open && ws.split.secondary) return workspace.items.find((it) => it.id === ws.split.secondary) || null;
    // "Beside the chat": while an activity fills the stage, the current conversation stays open beside it.
    if (layout.activityPlacement === 'beside-chat' && ACTIVITY_TABS.has(activeTab) && ws.active) return workspace.items.find((it) => it.id === ws.active) || null;
    return null;
  }, [workspace.ws, workspace.items, layout.activityPlacement, activeTab]);
  const splitShown = splitAvailable && !!secondaryItem;
  const [focusedPane, setFocusedPane] = useState('primary');
  useEffect(() => {
    if (!splitShown) setFocusedPane('primary');
  }, [splitShown]);

  const toggleSplit = useCallback(() => {
    if (workspace.ws.split.open) workspace.closeSplit();
    else workspace.openSplit(workspace.ws.split.secondary);
  }, [workspace]);

  useWorkspaceShortcuts({
    enabled: !!server,
    workspace: { ...workspace, close: navigating(workspace.close), reopenClosed: navigating(workspace.reopenClosed) },
    onActivate: activateConversation,
    onToggleSplit: () => splitAvailable && toggleSplit(),
    onFocusOtherPane: () => {
      if (!splitShown) return;
      const next = focusedPane === 'primary' ? 'secondary' : 'primary';
      setFocusedPane(next);
      const root = document.querySelector(`.vx-pane-${next} textarea, .vx-pane-${next} [contenteditable="true"], .vx-pane-${next} input`);
      if (root && root.focus) root.focus();
    },
  });

  // ---------- layout ----------
  const sidebarResize = usePanelResize({
    value: layout.sidebar.width,
    min: 260,
    max: 560,
    invert: layout.sidebar.side === 'right',
    onCommit: (v) => (pz.layoutEditing ? pz.setEditValue('sidebar.width', v) : pz.setLayoutValue('sidebar.width', v)),
  });
  const membersResize = usePanelResize({
    value: layout.members.width,
    min: 200,
    max: 420,
    invert: layout.members.side === 'right',
    onCommit: (v) => (pz.layoutEditing ? pz.setEditValue('members.width', v) : pz.setLayoutValue('members.width', v)),
  });
  const splitResize = usePanelResize({
    value: layout.split.ratio,
    min: 0.2,
    max: 0.8,
    ratio: true,
    vertical: layout.split.mode === 'down',
    invert: !!workspace.ws.split.swapped,
    onCommit: (v) => pz.setLayoutValue('split.ratio', Math.round(v * 100) / 100),
  });

  // Showing/hiding a panel changes this server's override when it has one, otherwise the account setting.
  const setPanelVisible = useCallback(
    (panel, visible) => {
      const key = `layout.${panel}.visible`;
      if (pz.layoutEditing) pz.setEditValue(`${panel}.visible`, visible);
      else if (serverKeyNow && Object.prototype.hasOwnProperty.call(pz.activeOverrides, key)) pz.setServerValue(serverKeyNow, key, visible);
      else pz.setLayoutValue(`${panel}.visible`, visible);
    },
    [pz, serverKeyNow]
  );

  // Server list badges follow each server's "unread badge" choice.
  const badgePrefs = pz.serverOverrides;

  const serverNavMode = useServerNavMode();
  const railNavEnabled = serverNavMode === SERVER_NAV_SIDEBAR;

  const { serverUnreadById: rawUnreadById, serverMentionCountById: rawMentionCountById } = useServerReadSummary({
    enabled: serversLoaded && Array.isArray(servers) && servers.length > 0,
  });
  const { serverUnreadById, serverMentionCountById } = useMemo(() => {
    const unread = { ...rawUnreadById };
    const mentions = { ...rawMentionCountById };
    for (const [key, ov] of Object.entries(badgePrefs || {})) {
      const mode = ov && ov['notifications.badges'];
      if (mode === 'none') {
        unread[key] = 0;
        mentions[key] = 0;
      } else if (mode === 'mentions') unread[key] = 0;
    }
    return { serverUnreadById: unread, serverMentionCountById: mentions };
  }, [rawUnreadById, rawMentionCountById, badgePrefs]);

  // Chats and the quick switcher (polls only while one of them is open).
  const conversationIndex = useConversationIndex({
    enabled: activeTab === 'chats' || switcherOpen || mobile,
    userId: myUid,
    servers,
    workspaceItems: workspace.items,
    serverUnreadById,
  });

  // Phones: incoming friend requests, for the DMs badge and the Requests section (desktop shows them in the top bar).
  const [friendRequestCount, setFriendRequestCount] = useState(0);
  useEffect(() => {
    if (!mobile || !myUid) return undefined;
    let alive = true;
    const load = () =>
      comsApiGet('/friend-requests')
        .then((r) => alive && setFriendRequestCount(Array.isArray(r && r.incoming) ? r.incoming.length : 0))
        .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [mobile, myUid]);

  // Opening a server honours its "When I open this server" choice.
  const selectServer = useCallback(
    (sid, opts) => {
      const key = String(sid || '').trim();
      const landing = key && pz.serverOverrides[key] ? pz.serverOverrides[key]['nav.landingChannel'] : '';
      const target = landing && Array.isArray(servers) ? servers.find((s) => extractServerKey(s) === key) : null;
      if (landing && target && Array.isArray(target.channels) && target.channels.some((c) => String(c.id) === landing)) {
        goTo(key, landing);
        return;
      }
      onSelectServer(sid, opts);
    },
    [pz.serverOverrides, servers, goTo, onSelectServer]
  );

  const onServersChanged = useCallback(async () => {
    await refreshServers(true);
  }, [refreshServers]);

  const handleOpenServerSettings = useCallback(
    (sid) => {
      const nextId = String(sid || '').trim();
      if (nextId && nextId !== String(activeServerId || '').trim()) {
        try {
          localStorage.setItem(LAST_COMMS_SERVER_KEY, nextId);
        } catch {}
        selectServer(nextId, { mobile: false, closePanelsRef: null });
      }
      setActiveTab('server-settings');
    },
    [activeServerId, selectServer]
  );

  // Opens an item found through search, bookmarks or resources: { kind, server_key, channel_id, message_id,
  // thread_id, file_id, source_id, ext_id, resource_id, canvas_id }. Messages scroll to the message, loading
  // older history when needed (ChatView picks up window.__voxPendingJump).
  useEffect(() => {
    const onTarget = (e) => {
      const t = (e && e.detail) || {};
      closeDrawersOnPhone();
      const key = String(t.server_key || '').trim();
      if (t.kind === 'message' || t.kind === 'channel') {
        if (!key) return;
        const cid = `${t.channel_kind === 'voice' ? 'voice' : 'text'}:${Number(t.channel_id)}`;
        if (t.kind === 'message') window.__voxPendingJump = { channelId: Number(t.channel_id), messageId: Number(t.message_id), at: Date.now() };
        setActiveTab('coms');
        goTo(key, cid);
        if (t.kind === 'message') setTimeout(() => window.dispatchEvent(new CustomEvent('vox-pending-jump')), 60);
        return;
      }
      if (t.kind === 'server') {
        if (key) selectServer(key, { mobile: false, closePanelsRef: null });
        setActiveTab('coms');
        return;
      }
      if (t.kind === 'dm' || t.kind === 'dm_message') {
        if (t.kind === 'dm_message') window.__voxPendingDmJump = { threadId: Number(t.thread_id), messageId: Number(t.message_id), at: Date.now() };
        setActiveTab('social');
        setSocialSection('dms');
        setSelectedSocialThreadId(String(t.thread_id));
        return;
      }
      if (t.kind === 'file' || t.kind === 'external' || t.kind === 'resource') {
        if (key && key !== String(activeServerId || '').trim()) selectServer(key, { mobile: false, closePanelsRef: null });
        const ctx =
          t.kind === 'resource'
            ? { area: 'library', tab: 'resources', resource_id: t.resource_id }
            : t.kind === 'file'
              ? { area: 'library', tab: 'files', source: { kind: 'internal' }, file_id: t.file_id }
              : { area: 'library', tab: 'files', source: { kind: 'source', id: Number(t.source_id), ext_id: Number(t.ext_id) } };
        setOrgContext({ ...ctx, at: Date.now() });
        setActiveTab('library');
        setTimeout(() => window.dispatchEvent(new CustomEvent('vox-open-org', { detail: ctx })), 0);
        return;
      }
      if (t.kind === 'canvas') {
        setActiveTab('canvas');
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('canvasId', String(Number(t.canvas_id)));
          navigate(`${url.pathname}${url.search}`);
        } catch {}
      }
    };
    window.addEventListener('vox-open-target', onTarget);
    return () => window.removeEventListener('vox-open-target', onTarget);
  }, [goTo, selectServer, activeServerId, navigate, closeDrawersOnPhone]);

  // Interaction cards (events and countdowns linked to a voice channel) offer "Join voice": only on a click,
  // never automatically.
  useEffect(() => {
    const onJoin = (e) => {
      const d = (e && e.detail) || {};
      const key = String(d.server_key || '').trim();
      const cid = Number(d.channel_id) || 0;
      if (!key || !cid) return;
      joinVoiceChannel(key, String(cid), String(cid));
    };
    window.addEventListener('vox-join-voice', onJoin);
    return () => window.removeEventListener('vox-join-voice', onJoin);
  }, [joinVoiceChannel]);

  const activeChannelDbId = useMemo(() => extractChannelDbId(activeChannel), [activeChannel]);
  const activeChannelCanHavePins = useMemo(() => channelCanHavePinnedMessages(activeChannel), [activeChannel]);

  useEffect(() => {
    let cancelled = false;

    async function loadPinnedMessages() {
      if (!server || !activeChannelReady || !activeChannelCanHavePins || !activeChannelDbId) {
        if (!cancelled) setPinnedMessages([]);
        return;
      }

      try {
        const collected = [];
        const seen = new Set();
        let beforeId = 0;
        let pageCount = 0;
        const maxPages = 25;
        const pageSize = 200;

        while (!cancelled && pageCount < maxPages) {
          const qs = beforeId > 0 ? `?limit=${pageSize}&before_id=${beforeId}` : `?limit=${pageSize}`;
          const res = await comsApiGet(`/text-channels/${encodeURIComponent(String(activeChannelDbId))}/messages${qs}`).catch(() => null);
          const rows = safeArray(res && res.messages).map(normalizePinnedMessage);

          if (!rows.length) break;

          for (const row of rows) {
            const id = toInt(row && row.id, 0);
            if (!id || seen.has(id)) continue;
            seen.add(id);
            if (toInt01(row && row.is_pinned) === 1) collected.push(row);
          }

          const oldest = rows.reduce((min, row) => {
            const id = toInt(row && row.id, 0);
            if (!id) return min;
            if (!min) return id;
            return id < min ? id : min;
          }, 0);

          pageCount += 1;

          if (!oldest || rows.length < pageSize) break;
          beforeId = oldest;
        }

        if (cancelled) return;

        collected.sort((a, b) => b.id - a.id);
        setPinnedMessages(collected);
      } catch {
        if (!cancelled) setPinnedMessages([]);
      }
    }

    loadPinnedMessages();

    return () => {
      cancelled = true;
    };
  }, [server, activeChannelReady, activeChannelCanHavePins, activeChannelDbId]);

  useEffect(() => {
    let cancelled = false;

    async function loadHeaderMessage() {
      if (!server || !activeChannelReady || !activeChannelCanHavePins || !activeChannelDbId) {
        if (!cancelled) {
          setHeaderMessage(null);
          setHeaderBarOpen(true);
        }
        return;
      }

      try {
        let found = null;
        let beforeId = 0;
        let pageCount = 0;
        const maxPages = 25;
        const pageSize = 200;

        while (!cancelled && pageCount < maxPages) {
          const qs = beforeId > 0 ? `?limit=${pageSize}&before_id=${beforeId}` : `?limit=${pageSize}`;
          const res = await comsApiGet(`/text-channels/${encodeURIComponent(String(activeChannelDbId))}/messages${qs}`).catch(() => null);
          const rows = safeArray(res && res.messages).map(normalizePinnedMessage);

          if (!rows.length) break;

          for (let i = rows.length - 1; i >= 0; i -= 1) {
            const row = rows[i];
            if (toInt01(row && row.is_header_message) === 1) {
              found = row;
              break;
            }
          }

          if (found) break;

          const oldest = rows.reduce((min, row) => {
            const id = toInt(row && row.id, 0);
            if (!id) return min;
            if (!min) return id;
            return id < min ? id : min;
          }, 0);

          pageCount += 1;

          if (!oldest || rows.length < pageSize) break;
          beforeId = oldest;
        }

        if (cancelled) return;

        setHeaderMessage(found || null);
        if (found && found.id > 0) {
          setHeaderBarOpen(readHeaderBarOpen(activeChannelDbId, found.id));
        } else {
          setHeaderBarOpen(true);
        }
      } catch {
        if (!cancelled) {
          setHeaderMessage(null);
          setHeaderBarOpen(true);
        }
      }
    }

    loadHeaderMessage();

    return () => {
      cancelled = true;
    };
  }, [server, activeChannelReady, activeChannelCanHavePins, activeChannelDbId]);

  useEffect(() => {
    const messageId = toInt(headerMessage && headerMessage.id, 0);
    const channelId = toInt(activeChannelDbId, 0);
    if (!messageId || !channelId) return;
    writeHeaderBarState(channelId, messageId, headerBarOpen);
  }, [headerMessage, activeChannelDbId, headerBarOpen]);

  const handleMessageMetaChanged = useCallback((msg) => {
    const row = normalizePinnedMessage(msg);
    const messageId = toInt(row && row.id, 0);
    const channelId = toInt(row && row.channel_id, 0);
    const activeId = toInt(activeChannelDbId, 0);

    if (!messageId || !channelId || !activeId || channelId != activeId) return;

    setPinnedMessages((prev) => {
      const existing = safeArray(prev).map(normalizePinnedMessage);
      const filtered = existing.filter((item) => toInt(item && item.id, 0) !== messageId);
      if (toInt01(row && row.is_pinned) !== 1) {
        return filtered.sort((a, b) => b.id - a.id);
      }
      return [...filtered, row].sort((a, b) => b.id - a.id);
    });

    if (toInt01(row && row.is_header_message) === 1 || toInt01(row && row.is_super_pinned) === 1 || toInt01(row && row.is_motd) === 1) {
      setHeaderMessage(row);
      setHeaderBarOpen(readHeaderBarOpen(activeId, messageId));
      return;
    }

    setHeaderMessage((prev) => {
      const prevId = toInt(prev && prev.id, 0);
      if (prevId === messageId) return null;
      return prev;
    });
  }, [activeChannelDbId]);

  const handleMessageRemoved = useCallback((msg) => {
    const row = normalizePinnedMessage(msg);
    const messageId = toInt(row && row.id, 0);
    const channelId = toInt(row && row.channel_id, 0);
    const activeId = toInt(activeChannelDbId, 0);

    if (!messageId || !channelId || !activeId || channelId != activeId) return;

    setPinnedMessages((prev) =>
      safeArray(prev)
        .map(normalizePinnedMessage)
        .filter((item) => toInt(item && item.id, 0) !== messageId)
        .sort((a, b) => b.id - a.id)
    );

    setHeaderMessage((prev) => {
      const prevId = toInt(prev && prev.id, 0);
      if (prevId === messageId) return null;
      return prev;
    });
  }, [activeChannelDbId]);

  const handleJumpToPinnedMessage = useCallback((msg) => {
    const messageId = toInt(msg && msg.id, 0);
    const channelId = toInt(activeChannelDbId, 0);
    if (!messageId || !channelId) return;

    try {
      window.dispatchEvent(
        new CustomEvent('voxemera-jump-to-message', {
          detail: {
            messageId,
            channelId,
            serverId: String(server && server.id != null ? server.id : ''),
          },
        })
      );
    } catch {}
  }, [activeChannelDbId, server]);

  const handleRemovePinnedMessage = useCallback(async (msg) => {
    const messageId = toInt(msg && msg.id, 0);
    if (!messageId) return false;

    try {
      await comsApiPatch(`/messages/${encodeURIComponent(String(messageId))}`, { is_pinned: 0 });

      setPinnedMessages((prev) =>
        safeArray(prev)
          .map(normalizePinnedMessage)
          .filter((row) => toInt(row && row.id, 0) !== messageId)
      );

      return true;
    } catch {
      return false;
    }
  }, []);

  const handleCloseHeaderBar = useCallback(() => {
    setHeaderBarOpen(false);
  }, []);

  const handleJumpToHeaderMessage = useCallback(() => {
    if (!headerMessage) return;
    handleJumpToPinnedMessage(headerMessage);
  }, [headerMessage, handleJumpToPinnedMessage]);

  const handleCreateServer = useCallback(
    async (payload) => {
      try {
        const res = await comsApiPost('/servers', payload);
        setCreateServerOpen(false);

        // POST /servers responds with { ok, server: { id: <public id> } }.
        const nextId =
          res && res.server && res.server.id != null
            ? String(res.server.id)
            : res && res.server_key != null
              ? String(res.server_key)
              : res && res.public_id != null
                ? String(res.public_id)
                : res && res.server_id != null
                  ? String(res.server_id)
                  : '';

        if (res && res.ok && nextId) {
          try {
            localStorage.setItem(LAST_COMMS_SERVER_KEY, nextId);
          } catch {}

          await refreshServers(true);
          setActiveServerId(nextId);
          navigate(`/comms/${nextId}`);
        }
      } catch {
        setCreateServerOpen(false);
      }
    },
    [refreshServers, setActiveServerId, navigate]
  );

  // A server created elsewhere in the app (e.g. imported from a Discord template): load it and open it.
  useEffect(() => {
    const onOpen = async (e) => {
      const key = String((e && e.detail && e.detail.serverKey) || '').trim();
      if (!key) return;
      try {
        localStorage.setItem(LAST_COMMS_SERVER_KEY, key);
      } catch {}
      setCreateServerOpen(false);
      await refreshServers(true);
      setActiveServerId(key);
      navigate(`/comms/${key}`);
    };
    window.addEventListener('vox-open-server', onOpen);
    return () => window.removeEventListener('vox-open-server', onOpen);
  }, [refreshServers, setActiveServerId, navigate]);

  const handleCreateChannel = useCallback(
    async (payload) => {
      if (!server) return;
      try {
        await comsApiPost(`/servers/${encodeURIComponent(String(server.id))}/channels`, payload);
        window.dispatchEvent(new CustomEvent('vox-channels-changed'));
        setCreateChannelOpen(false);
        await refreshServers(true);
      } catch {
        setCreateChannelOpen(false);
      }
    },
    [server, refreshServers]
  );



  const openCanvasMobileTools = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent('canvas-mobile-open-tools'));
    } catch {}
  }, []);

  const openCanvasMobileLayers = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent('canvas-mobile-open-layers'));
    } catch {}
  }, []);

  if (!server) {
    return (
      <ComsEmptyState
        servers={servers}
        serversLoaded={serversLoaded}
        apiAvailable={apiAvailable}
        authState={authState}
        lastApiError={lastApiError}
        joinInviteCode={joinInviteCode}
        setJoinInviteCode={setJoinInviteCode}
        joinBusy={joinBusy}
        joinErr={joinErr}
        openJoinOnEnter={openJoinOnEnter}
        handleJoinServer={handleJoinServer}
        createServerOpen={createServerOpen}
        setCreateServerOpen={setCreateServerOpen}
        handleCreateServer={handleCreateServer}
        refreshServers={refreshServers}
        navigate={navigate}
      />
    );
  }


  const onToggleMembers = () => {
    if (mobile) {
      setRightOpen((v) => !v);
      return;
    }
    setPanelVisible('members', !layout.members.visible);
  };

  const openCreateServerModal = () => {
    setCreateServerOpen(true);
  };

  const renderSidebar = (ctx = {}) => {
    if (activeTab === 'social') {
      return (
        <SocialSidebar
          server={server}
          servers={servers}
          user={user}
          onSelectServer={(sid) => {
            setActiveTab('social');
            try {
              localStorage.setItem(LAST_COMMS_SERVER_KEY, String(sid || '').trim());
            } catch {}
            selectServer(sid, { mobile: false, closePanelsRef: null });
          }}
          onCreateServer={openCreateServerModal}
          onOpenTab={(t) => {
            setActiveTab(t);
            if (ctx.mobile && typeof ctx.closePanels === 'function') ctx.closePanels();
          }}
          socialSection={socialSection}
          onSelectSocialSection={setSocialSection}
          selectedSocialThreadId={selectedSocialThreadId}
          onSelectSocialThreadId={setSelectedSocialThreadId}
          serverUnreadById={serverUnreadById}
          serverMentionCountById={serverMentionCountById}
          hideServerDrawer={railNavEnabled}
          onOpenServerSettings={handleOpenServerSettings}
          onServersChanged={onServersChanged}
        />
      );
    }

    return (
      <ServerSidebar
        server={server}
        servers={servers}
        activeChannelId={activeChannelId}
        onSelectChannel={(cid) => {
          setActiveTab('coms');
          onSelectChannel(cid);
          // Phones: choosing a channel (also the one already open) returns to the conversation.
          if (ctx.mobile && typeof ctx.closePanels === 'function') ctx.closePanels();
        }}
        onSelectServer={(sid) => {
          const nextId = String(sid || '').trim();
          if (nextId) {
            try {
              localStorage.setItem(LAST_COMMS_SERVER_KEY, nextId);
            } catch {}
          }
          setActiveTab(keepTabOnServerSwitch);
          selectServer(sid, { mobile: false, closePanelsRef: null });
        }}
        onOpenTab={(t) => {
          setActiveTab(t);
          if (ctx.mobile && typeof ctx.closePanels === 'function') ctx.closePanels();
        }}
        onCreateChannel={() => setCreateChannelOpen(true)}
        onCreateServer={openCreateServerModal}
        user={user}
        apiAvailable={apiAvailable}
        onChannelsChanged={onChannelsChanged}
        voiceSession={voiceSession}
        onJoinVoiceChannel={joinVoiceChannel}
        onLeaveVoiceChannel={leaveVoiceChannel}
        onToggleVoiceMute={onToggleVoiceMute}
        onToggleVoiceDeafen={onToggleVoiceDeafen}
        voiceShowVideo={voiceShowVideo}
        onToggleVoiceVideo={toggleVoiceVideo}
        voiceVideoMode={voiceVideoMode}
        voiceVideoErr={voiceVideoErr}
        onToggleVoiceCamera={toggleVoiceCamera}
        onToggleVoiceScreen={toggleVoiceScreen}
        serverUnreadById={serverUnreadById}
        serverMentionCountById={serverMentionCountById}
        hideServerDrawer={railNavEnabled}
        onOpenServerSettings={handleOpenServerSettings}
        onServersChanged={onServersChanged}
        gamesActive={activeTab === 'games'}
        activeGame={activeGame}
        onOpenGames={() => {
          setActiveTab('games');
          if (ctx.mobile && typeof ctx.closePanels === 'function') ctx.closePanels();
        }}
      />
    );
  };

  // Optional vertical server list. Shares server data, selection and actions with the
  // dropdown; switching layouts only changes which view is mounted, so voice and the
  // active channel are unaffected.
  const renderRail = () => {
    if (!railNavEnabled) return null;
    return (
      <ServerRail
        servers={servers}
        activeServerId={activeServerId}
        onSelectServer={(sid) => {
          const nextId = String(sid || '').trim();
          if (!nextId) return;
          try {
            localStorage.setItem(LAST_COMMS_SERVER_KEY, nextId);
          } catch {}
          if (activeTab !== 'social') setActiveTab(keepTabOnServerSwitch);
          selectServer(nextId, { mobile: false, closePanelsRef: null });
        }}
        onCreateServer={openCreateServerModal}
        onServersChanged={onServersChanged}
        onOpenServerSettings={handleOpenServerSettings}
        apiAvailable={apiAvailable}
        user={user}
        serverUnreadById={serverUnreadById}
        serverMentionCountById={serverMentionCountById}
      />
    );
  };

  const renderRightPanel = () => {
    return (
      <MembersPanel
        server={server}
        user={user}
        messageSearch={messageSearch}
        onMessageSearchChange={setMessageSearch}
        panelTab={rightPanelTab}
        onPanelTabChange={setRightPanelTab}
        pinnedMessages={pinnedMessages}
        onJumpToPinnedMessage={handleJumpToPinnedMessage}
        onRemovePinnedMessage={handleRemovePinnedMessage}
      />
    );
  };

  const showMembersPanel = activeTab === 'coms';

  const headerBarVisible =
    layout.headerBar &&
    activeTab === 'coms' &&
    activeChannelCanHavePins &&
    !!headerMessage &&
    toInt(headerMessage && headerMessage.id, 0) > 0 &&
    headerBarOpen;

  const headerPreview = headerMessage ? buildHeaderPreview(headerMessage, mobile ? 120 : 220) : '';
  const headerAuthor = String(headerMessage && headerMessage.author ? headerMessage.author : '').trim();
  const headerStamp = String(headerMessage && headerMessage.stamp ? headerMessage.stamp : '').trim();

  return (
    <>
    <ToastHost />
    <ReportHost />
    <InteractionHost />
    {switcherOpen ? (
      <QuickSwitcher index={conversationIndex} onOpen={openConversation} onClose={() => setSwitcherOpen(false)} activeId={locationConvId} pinnedIds={pinnedConversationIds} />
    ) : null}
    <ComsMobileShell
      mobile={mobile}
      sidebarWidth={sidebarResize.size}
      sidebarResizing={sidebarResize.active}
      shellClassName={[
        !mobile && layout.uiScale !== 1 && !UNSCALED_TABS.has(activeTab) ? 'vx-ui-scaled' : '',
        layout.sidebar.side === 'right' ? 'vx-sidebar-right' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      sidebarHidden={!mobile && activeTab !== 'canvas' && !layout.sidebar.visible}
      onShowSidebar={() => setPanelVisible('sidebar', true)}
      sidebarTag={<RegionTag region="sidebar" />}
      onSidebarClickCapture={onSidebarClickCapture}
      closePanelsExternalRef={closePanelsRef}
      leftOpen={leftOpen}
      setLeftOpen={setLeftOpen}
      rightOpen={rightOpen}
      setRightOpen={setRightOpen}
      renderRail={renderRail}
      renderSidebar={renderSidebar}
      renderRightPanel={renderRightPanel}
      onStartResizeSidebar={sidebarResize.onStart}
      onResizeSidebarKey={sidebarResize.onKeyDown}
      showDesktopSidebar={activeTab !== 'canvas' && layout.sidebar.visible}
      // No swipes where the finger draws or plays: Canvas, Activities (games), Studio and Maps.
      enableMainSwipeOpen={!['canvas', 'games', 'studio', 'maps'].includes(activeTab) && pz.mobilePrefs.swipe !== false}
      gestures={pz.mobilePrefs.gestures}
      onGesture={(action) => gestureHandlerRef.current(action)}
    >
      {({
        mobile: isMob,
        membersDrawerOpen,
        closePanels,
        openLeftPanel,
        openRightPanel,
        toggleLeftPanel,
        mobileMetrics,
      }) => {
        const mobilePrefs = pz.mobilePrefs;
        const navParts = layoutParts(mobilePrefs);
        const shownBarItems = barItems(mobilePrefs.nav, mobilePrefs.navOverflow);
        // Navigation destinations (bar, menu or gesture): the same tabs, drawers and areas the rest of the app opens.
        const navigateMobile = (id) => {
          if (id === 'more' && navParts.topBar) {
            // Navigation at the top: More drops down from there instead of rising from the bottom.
            closePanels();
            setMoreOpen(false);
            setNavMenuOpen((v) => !v);
            return;
          }
          setNavMenuOpen(false);
          if (id === 'more') {
            closePanels();
            setMoreOpen((v) => !v);
            return;
          }
          setMoreOpen(false);
          if (id === 'servers') {
            if (leftOpen) {
              closePanels();
              return;
            }
            // From DMs the drawer would list friends and DMs; Servers always means servers and channels.
            if (activeTab === 'social' || activeTab === 'chats') setActiveTab('coms');
            openLeftPanel();
            return;
          }
          if (id === 'members') {
            setActiveTab('coms');
            if (rightOpen) closePanels();
            else openRightPanel();
            return;
          }
          closePanels();
          if (id === 'chats') setActiveTab('chats');
          else if (id === 'chat') setActiveTab('coms');
          else if (id === 'dms') {
            // DMs always lands on the list of conversations (Messages), not inside the last one.
            setActiveTab('social');
            setSocialSection('dms');
            setSelectedSocialThreadId('');
          } else if (id === 'activities') setActiveTab('games');
          else if (id === 'watch') window.dispatchEvent(new CustomEvent('vox-open-games', { detail: { category: 'watch' } }));
          else if (id === 'canvas') setActiveTab('canvas');
          else if (id === 'studio') setActiveTab('studio');
          else if (id === 'maps') setActiveTab('maps');
          else if (id === 'files') openOrgArea('library');
          else if (id === 'search') openOrgArea('search');
          else if (id === 'marketplace') setActiveTab('marketplace');
          else if (id === 'voice') {
            // In a call: back to its channel. Otherwise the channel list, where the voice channels are.
            if (voiceWhere.serverKey && voiceWhere.channelDbId && voiceDockState(voiceSession).inVoice) {
              openConversationTarget({ kind: 'channel', server_key: voiceWhere.serverKey, channel_kind: 'voice', channel_id: voiceWhere.channelDbId });
            } else {
              if (activeTab === 'social' || activeTab === 'chats') setActiveTab('coms');
              openLeftPanel();
            }
          } else if (id === 'profile' || id === 'settings') {
            try {
              window.__voxPendingSettingsTab = id === 'profile' ? 'account' : '';
            } catch {}
            setActiveTab('settings');
            if (id === 'profile') window.dispatchEvent(new CustomEvent('vox-open-settings', { detail: { tab: 'account' } }));
          }
        };

        // ---------- the phone header: always names where you are ----------
        const AREA_TITLES = {
          chats: 'Chats',
          games: 'Activities',
          watch: 'Watch Together',
          canvas: 'Canvas',
          studio: 'Studio',
          maps: 'Maps',
          marketplace: 'Marketplace',
          library: 'Files',
          search: 'Search',
          bookmarks: 'Saved',
          'server-events': 'Events',
          'server-settings': 'Server Settings',
          settings: 'Settings',
        };
        const serverName = server && server.name ? String(server.name) : '';
        const switcherAction = { id: 'switcher', icon: <NavIcon id="switch" />, label: 'Switch conversation', onClick: () => setSwitcherOpen(true) };
        const openServersLeft = {
          kind: 'server',
          label: serverName ? `${serverName}: servers and channels` : 'Servers and channels',
          name: serverName,
          logo: serverLogo,
          onClick: () => (leftOpen ? closePanels() : openLeftPanel()),
        };
        const mobileHeaderProps = (() => {
          if (activeTab === 'coms') {
            const voice = activeChannel && activeChannel.kind === 'voice';
            return {
              left: openServersLeft,
              title: activeChannel && activeChannel.name ? String(activeChannel.name) : serverName || 'Channel',
              titlePrefix: activeChannel ? (voice ? '🔊' : '#') : '',
              subtitle: activeChannel ? serverName : '',
              actions: [
                switcherAction,
                { id: 'members', icon: <NavIcon id="members" />, label: rightOpen ? 'Hide members' : 'Show members', onClick: onToggleMembers, pressed: !!rightOpen },
              ],
            };
          }
          if (activeTab === 'social') {
            const thread = socialSection === 'dms' && selectedSocialThreadId ? conversationIndex.all.find((it) => it.id === `d:${selectedSocialThreadId}`) : null;
            if (socialSection === 'dms' && selectedSocialThreadId) {
              return {
                left: { kind: 'back', label: 'All direct messages', onClick: () => setSelectedSocialThreadId('') },
                title: thread ? thread.title : 'Direct message',
                avatar: thread ? { src: thread.avatarUrl, label: thread.title } : null,
                // On one line (slim header) the name needs the room; the avatar and back arrow already say DM.
                subtitle: mobilePrefs.headerSize === 'slim' ? '' : 'Direct message',
                actions: [{ id: 'dm-search', icon: <NavIcon id="search" />, label: 'Search this conversation', onClick: () => openOrgArea('search', { scope: 'dm', dm: selectedSocialThreadId }) }, switcherAction],
              };
            }
            return { title: 'DMs', actions: [switcherAction] };
          }
          if (activeTab === 'canvas') {
            return {
              title: 'Canvas',
              subtitle: serverName,
              actions: [
                { id: 'canvas-tools', icon: <NavIcon id="settings" />, label: 'Open canvas tools', onClick: openCanvasMobileTools },
                { id: 'canvas-layers', icon: <NavIcon id="files" />, label: 'Open canvas layers', onClick: openCanvasMobileLayers },
              ],
            };
          }
          const perServer = ['games', 'watch', 'library', 'search', 'bookmarks', 'server-events', 'server-settings'].includes(activeTab);
          return {
            left: perServer ? openServersLeft : null,
            title: AREA_TITLES[activeTab] || 'Voxemera',
            subtitle: perServer ? serverName : '',
            actions: activeTab === 'chats' ? [] : [switcherAction],
          };
        })();
        // Settings on phones have their own title bar with Done; the header stays only when navigation lives in it.
        const mobileHeaderShown = activeTab !== 'settings' || navParts.topBar || navParts.menuFromHeader;
        // The conversation on screen counts as read at once (the server's read state catches up on the next poll).
        const unreadElsewhere = conversationIndex.unread.filter((it) => it.id !== locationConvId);
        const navBadges = { dms: friendRequestCount || unreadElsewhere.some((it) => it.kind === 'dm') || undefined, chats: unreadElsewhere.length || undefined };
        const navBarProps = {
          nav: mobilePrefs.nav,
          style: mobilePrefs.navStyle,
          size: mobilePrefs.navSize,
          overflow: mobilePrefs.navOverflow,
          behaviour: mobilePrefs.navBehaviour,
          hidden: navHidden,
          activeTab,
          leftOpen,
          rightOpen,
          moreOpen: moreOpen || (navParts.topBar && navMenuOpen),
          onNavigate: navigateMobile,
          badges: navBadges,
        };
        const openMobileLayoutSettings = () => {
          setMoreOpen(false);
          setNavMenuOpen(false);
          try {
            window.__voxPendingSettingsTab = 'mobile';
          } catch {}
          setActiveTab('settings');
          window.dispatchEvent(new CustomEvent('vox-open-settings', { detail: { tab: 'mobile' } }));
        };
        // Swipe gestures (Mobile Layout → Gestures) run the same actions as the buttons.
        gestureHandlerRef.current = (action) => {
          if (action === 'servers') navigateMobile('servers');
          else if (action === 'members') {
            if (activeTab === 'coms') openRightPanel();
          } else if (action === 'chats') navigateMobile('chats');
          else if (action === 'switcher') setSwitcherOpen(true);
          else if (action === 'menu') {
            if (navParts.bottomBar) setMoreOpen(true);
            else setNavMenuOpen(true);
          } else if (action === 'prev') stepConversation(-1);
          else if (action === 'next') stepConversation(1);
        };
        const toggleNavMenu = () => {
          closePanels();
          setMoreOpen(false);
          setNavMenuOpen((v) => !v);
        };
        const headerFull = {
          ...mobileHeaderProps,
          bannerServer: activeTab === 'coms' && serverKeyNow ? serverKeyNow : '',
          size: mobilePrefs.headerSize,
          // Dropdown and Gestures: the conversation name opens the navigation menu.
          menu: navParts.menuFromHeader || navParts.handle ? { open: navMenuOpen, onToggle: toggleNavMenu, label: `${mobileHeaderProps.title}: open navigation` } : null,
          children: navParts.topBar ? <MobileNavBar {...navBarProps} position="top" /> : null,
        };
        // Conversations offered first in the menu: unread, pinned, then recent.
        const menuConversations = (() => {
          const seen = new Set();
          return [...conversationIndex.unread, ...conversationIndex.pinned, ...conversationIndex.recent].filter((it) => !seen.has(it.id) && seen.add(it.id));
        })();

        const mobileMainLeftPad = 0;
        const mobileMainRightPad = 0;
        const mobileTopPad = isMob && !mobileHeaderShown ? 'env(safe-area-inset-top, 0px)' : 0;
        // The bottom bar (always on phones) covers the gesture area itself; while it hides for the keyboard the
        // keyboard covers it instead.
        const mobileBottomPad = 0;

        // Phones show conversation tabs only inside a conversation (not over the DM list, Chats or Activities).
        const tabBarShown =
          workspace.items.length > 0 &&
          (isMob ? !!locationConvId : activeTab === 'coms' || activeTab === 'social' || ACTIVITY_TABS.has(activeTab)) &&
          (layout.tabBar === 'always' || (layout.tabBar === 'auto' && workspace.items.length > 1));
        const primaryContent = (
          <>
            {gamesMounted ? (
              <div
                data-testid="games-host"
                aria-hidden={activeTab === 'games' ? undefined : 'true'}
                style={{ display: activeTab === 'games' ? 'flex' : 'none', height: '100%', width: '100%', minWidth: 0 }}
              >
                <GamesView
                  user={user}
                  server={server}
                  visible={activeTab === 'games'}
                  pendingCode={pendingGameCode}
                  onPendingCodeHandled={() => setPendingGameCode('')}
                  onOpenTab={setActiveTab}
                  servers={servers}
                  voiceSession={voiceSession}
                  voiceRoom={voiceRoomObj}
                  onJoinVoice={(serverPublicId, channelId) => joinVoiceChannel(String(serverPublicId), String(channelId), String(channelId))}
                />
              </div>
            ) : null}
            {activeTab === 'chats' ? (
              <ChatsView index={conversationIndex} onOpen={openConversation} onPin={pinConversation} pinnedIds={pinnedConversationIds} activeId={locationConvId} />
            ) : isMob && activeTab === 'social' && !(socialSection === 'dms' && selectedSocialThreadId) ? (
              // Phones: DMs is a list of conversations (with Friends and Requests beside it) until one is opened.
              <div className="vx-social-mobile">
                <DmsSections section={socialSection} onSection={setSocialSection} requests={friendRequestCount} />
                {socialSection === 'dms' ? (
                  <MobileDmsView index={conversationIndex} onOpen={openConversation} onPin={pinConversation} pinnedIds={pinnedConversationIds} activeId={locationConvId} />
                ) : (
                  <TabRouter
                    tab="social"
                    server={server}
                    user={user}
                    apiAvailable={apiAvailable}
                    onOpenTab={setActiveTab}
                    socialSection={socialSection}
                    setSocialSection={setSocialSection}
                    selectedSocialThreadId={selectedSocialThreadId}
                    setSelectedSocialThreadId={setSelectedSocialThreadId}
                  />
                )}
              </div>
            ) : (
            <TabRouter
              tab={activeTab}
              server={server}
              channel={activeChannel}
              user={user}
              voiceSession={voiceSession}
              chatNameById={chatNameById}
              voiceRoomObj={voiceRoomObj}
              voiceShowVideo={voiceShowVideo}
              messageSearch={messageSearch}
              channelReady={activeChannelReady}
              onOpenTab={setActiveTab}
              apiAvailable={apiAvailable}
              socialSection={socialSection}
              setSocialSection={setSocialSection}
              selectedSocialThreadId={selectedSocialThreadId}
              setSelectedSocialThreadId={setSelectedSocialThreadId}
              onMessageMetaChanged={handleMessageMetaChanged}
              onMessageRemoved={handleMessageRemoved}
              conversationKey={locationConvId}
              chatAutoFocus={!splitShown || focusedPane === 'primary'}
              orgContext={orgContext}
              onCustomiseLayout={() => {
                setActiveTab('coms');
                pz.beginLayoutEditing();
              }}
            />
            )}
          </>
        );
        const swapped = splitShown && workspace.ws.split.swapped;
        const ratio = splitResize.size;
        return (
          <>
            <div
              className={`coms-workarea${layout.members.side === 'left' ? ' vx-members-left' : ''}`}
              style={
                isMob
                  ? {
                      display: 'flex',
                      minWidth: 0,
                      width: '100%',
                      height: mobileMetrics ? `${mobileMetrics.viewportHeight}px` : '100%',
                    }
                  : {
                      display: 'flex',
                      minWidth: 0,
                      // Takes the space left by the channel list (which is never squeezed below its width).
                      flex: '1 1 0',
                      height: '100%',
                    }
              }
            >
              <div
                className="coms-main"
                ref={mobileMainRef}
                data-vx-region="main"
                style={
                  isMob
                    ? {
                        flex: 1,
                        minWidth: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        paddingLeft: mobileMainLeftPad,
                        paddingRight: mobileMainRightPad,
                        paddingTop: mobileTopPad,
                        paddingBottom: mobileBottomPad,
                        marginLeft: 0,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                      }
                    : {
                        flex: 1,
                        minWidth: 0,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                      }
                }
              >
                {/* Phones: one header naming where you are; navigation is the bar (Mobile Layout), not a second menu. */}
                {isMob ? (
                  mobileHeaderShown ? <MobileHeader {...headerFull} /> : null
                ) : (
                  <div>
                    <TopTabs
                      server={server}
                      activeTab={activeTab}
                      onSelectTab={setActiveTab}
                      membersOpen={layout.members.visible}
                      onToggleMembers={onToggleMembers}
                      activeChannel={activeChannel}
                      user={user}
                      setUser={setUser}
                    />
                  </div>
                )}

                {!isMob && activeTab === 'coms' && serverKeyNow ? <ServerHeaderBanner serverKey={serverKeyNow} name={server && server.name ? String(server.name) : ''} /> : null}

                <ServerThemePrompt serverKey={serverKeyNow} serverName={server && server.name ? String(server.name) : ''} />

                {workspace.notice ? (
                  <div className="vx-notice" role="status" data-testid="workspace-notice">
                    {workspace.notice}
                    <button type="button" className="vx-btn small" onClick={workspace.clearNotice}>
                      Dismiss
                    </button>
                  </div>
                ) : null}

                {tabBarShown ? (
                  <ConversationTabStrip
                    items={workspace.items}
                    activeId={workspace.ws.active}
                    displayed={!!locationConvId && locationConvId === workspace.ws.active}
                    secondaryId={splitShown ? secondaryItem && secondaryItem.id : null}
                    mobile={isMob}
                    splitAvailable={splitAvailable && workspace.items.length > 1}
                    splitOpen={workspace.ws.split.open}
                    onActivate={activateConversation}
                    onClose={navigating(workspace.close)}
                    onCloseOthers={navigating(workspace.closeOthers)}
                    onMove={workspace.move}
                    onPin={workspace.pin}
                    onKeep={workspace.promote}
                    onOpenInSplit={(id) => workspace.setSecondary(id)}
                    onToggleSplit={toggleSplit}
                    onReopenClosed={navigating(workspace.reopenClosed)}
                    canReopen={workspace.ws.closed.length > 0}
                  />
                ) : null}

                {headerBarVisible ? (
                  <div
                    style={{
                      flexShrink: 0,
                      paddingLeft: isMob ? 8 : 10,
                      paddingRight: isMob ? 6 : 10,
                      paddingTop: 6,
                      paddingBottom: 6,
                      borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
                      background: isMob ? 'rgba(var(--vxs-2-rgb, 18,22,32), 0.96)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.02 * var(--vx-ov, 1)))',
                      backdropFilter: isMob ? 'blur(8px)' : 'none',
                    }}
                  >
                    <div
                      style={{
                        height: 34,
                        minHeight: 34,
                        borderRadius: 10,
                        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                        background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        paddingLeft: 10,
                        paddingRight: 8,
                        minWidth: 0,
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleJumpToHeaderMessage}
                        title="Jump to header message"
                        style={{
                          appearance: 'none',
                          border: 'none',
                          background: 'transparent',
                          color: 'inherit',
                          padding: 0,
                          margin: 0,
                          minWidth: 0,
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <span
                          style={{
                            flexShrink: 0,
                            fontSize: 12,
                            lineHeight: 1,
                            color: '#ff6767',
                            filter: 'drop-shadow(0 0 6px rgba(255,60,60,0.35))',
                          }}
                        >
                          📌
                        </span>

                        <span
                          style={{
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {headerAuthor ? (
                            <span
                              style={{
                                fontSize: 11,
                                opacity: 0.82,
                                flexShrink: 0,
                              }}
                            >
                              {headerAuthor}
                            </span>
                          ) : null}

                          {headerStamp ? (
                            <span
                              style={{
                                fontSize: 11,
                                opacity: 0.55,
                                flexShrink: 0,
                              }}
                            >
                              {headerStamp}
                            </span>
                          ) : null}

                          <span
                            style={{
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: 12,
                              opacity: 0.86,
                            }}
                          >
                            {headerPreview}
                          </span>
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={handleCloseHeaderBar}
                        title="Close header bar"
                        aria-label="Close header bar"
                        style={{
                          appearance: 'none',
                          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                          background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                          color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.84 * var(--vx-ov, 1)))',
                          width: 22,
                          height: 22,
                          borderRadius: 7,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          flexShrink: 0,
                          padding: 0,
                          lineHeight: 1,
                          fontSize: 12,
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ) : null}

                <div
                  className="coms-content"
                  style={
                    isMob
                      ? {
                          flex: 1,
                          minHeight: 0,
                          minWidth: 0,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                        }
                      : {
                          flex: 1,
                          minHeight: 0,
                          minWidth: 0,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                        }
                  }
                >
                  <div
                    className="coms-stage"
                    style={
                      isMob
                        ? {
                            flex: 1,
                            maxWidth: '100%',
                            width: '100%',
                            minWidth: 0,
                            minHeight: 0,
                            height: '100%',
                            margin: 0,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                          }
                        : {
                            flex: 1,
                            width: '100%',
                            minWidth: 0,
                            minHeight: 0,
                            height: '100%',
                            margin: 0,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                          }
                    }
                  >
                    {/* One container in both modes, so turning the split on or off never remounts the main view
                        (a running game, video or voice view keeps going). */}
                    <div
                      ref={splitResize.containerRef}
                      className={`vx-split${splitShown ? ' is-split' : ''}${layout.split.mode === 'down' ? ' vx-split-down' : ''}`}
                      data-testid="workspace-split"
                      data-split={splitShown ? layout.split.mode : 'none'}
                    >
                      <div
                        className={`vx-pane vx-pane-primary${splitShown && focusedPane === 'primary' ? ' is-focused' : ''}`}
                        style={splitShown ? { flex: `${ratio} 1 0`, order: swapped ? 3 : 1 } : { flex: '1 1 auto' }}
                        onPointerDownCapture={() => splitShown && setFocusedPane('primary')}
                        onFocusCapture={() => splitShown && setFocusedPane('primary')}
                        data-testid="pane-primary"
                      >
                        <RegionTag region="main" />
                        <div className="vx-pane-primary-body">{primaryContent}</div>
                      </div>
                      {splitShown ? (
                        <>
                          <div
                            className={`vx-resizer${layout.split.mode === 'down' ? ' vx-resizer-row' : ''}${splitResize.active ? ' active' : ''}`}
                            role="separator"
                            aria-orientation={layout.split.mode === 'down' ? 'horizontal' : 'vertical'}
                            aria-label="Resize split view"
                            aria-valuemin={20}
                            aria-valuemax={80}
                            aria-valuenow={Math.round(ratio * 100)}
                            tabIndex={0}
                            style={{ order: 2 }}
                            onPointerDown={splitResize.onStart}
                            onKeyDown={splitResize.onKeyDown}
                            data-testid="split-resizer"
                          />
                          <div
                            className={`vx-pane vx-pane-secondary${focusedPane === 'secondary' ? ' is-focused' : ''}`}
                            style={{ flex: `${1 - ratio} 1 0`, order: swapped ? 1 : 3 }}
                            onPointerDownCapture={() => setFocusedPane('secondary')}
                            onFocusCapture={() => setFocusedPane('secondary')}
                            data-testid="pane-secondary"
                          >
                            <SplitPane
                              item={secondaryItem}
                              items={workspace.items}
                              user={user}
                              server={server}
                              chatNameById={chatNameById}
                              focused={focusedPane === 'secondary'}
                              mode={layout.split.mode}
                              canSwap
                              onSelect={(id) => workspace.setSecondary(id)}
                              onSwap={() => {
                                if (secondaryItem && secondaryItem.id !== workspace.ws.active && locationConvId) navigating(workspace.swapSplit)();
                                else workspace.replace({ ...workspace.ws, split: { ...workspace.ws.split, swapped: !workspace.ws.split.swapped } });
                              }}
                              onToggleMode={() => pz.setLayoutValue('split.mode', layout.split.mode === 'down' ? 'right' : 'down')}
                              onClose={() => {
                                if (workspace.ws.split.open) workspace.closeSplit();
                                else pz.setLayoutValue('activityPlacement', 'main');
                              }}
                              onOpenInMain={activateConversation}
                            />
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
                {isMob && mobilePrefs.voiceDock === 'always' ? (
                  <MobileVoiceDock
                    voiceSession={voiceSession}
                    channelName={voiceWhere.channelName}
                    serverName={voiceWhere.serverKey !== serverKeyNow ? voiceWhere.serverName : ''}
                    onOpenChannel={() => {
                      closePanels();
                      setMoreOpen(false);
                      if (voiceWhere.serverKey && voiceWhere.channelDbId) openConversationTarget({ kind: 'channel', server_key: voiceWhere.serverKey, channel_kind: 'voice', channel_id: voiceWhere.channelDbId });
                    }}
                    onToggleMute={onToggleVoiceMute}
                    onToggleDeafen={onToggleVoiceDeafen}
                    onLeave={leaveVoiceChannel}
                  />
                ) : null}
                {/* Phone navigation at the bottom, as the layout chose: a bar, a menu launcher or a gesture handle. */}
                {isMob && navParts.bottomBar ? <MobileNavBar {...navBarProps} position="bottom" /> : null}
                {isMob && (navParts.bottomLauncher || navParts.handle) ? (
                  <MobileMenuLauncher kind={navParts.handle ? 'handle' : 'launcher'} open={navMenuOpen} label="Menu" onToggle={toggleNavMenu} />
                ) : null}
                {isMob && moreOpen ? (
                  <MobileMoreSheet
                    nav={navParts.topBar || navParts.bottomBar ? shownBarItems : []}
                    style={mobilePrefs.navStyle}
                    onNavigate={navigateMobile}
                    onClose={() => setMoreOpen(false)}
                    onCustomise={openMobileLayoutSettings}
                  />
                ) : null}
                {isMob && navMenuOpen && !navParts.bottomBar ? (
                  <MobileNavMenu
                    nav={mobilePrefs.nav}
                    direction={navParts.menuFromHeader || navParts.topBar ? 'down' : 'up'}
                    style={mobilePrefs.navStyle}
                    activeTab={activeTab}
                    conversations={menuConversations}
                    onOpenConversation={openConversation}
                    onNavigate={navigateMobile}
                    onClose={() => setNavMenuOpen(false)}
                    onCustomise={openMobileLayoutSettings}
                    badges={navBadges}
                  />
                ) : null}
              </div>

              {!isMob && layout.members.visible && showMembersPanel ? (
                <div className="vx-members-region" data-vx-region="members" style={{ width: membersResize.size, flexDirection: layout.members.side === 'left' ? 'row-reverse' : 'row' }}>
                  <div
                    className={`vx-resizer${membersResize.active ? ' active' : ''}`}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize member list"
                    aria-valuemin={200}
                    aria-valuemax={420}
                    aria-valuenow={Math.round(membersResize.size)}
                    tabIndex={0}
                    onPointerDown={membersResize.onStart}
                    onKeyDown={membersResize.onKeyDown}
                    data-testid="members-resizer"
                  />
                  <RegionTag region="members" />
                  <MembersPanel
                    server={server}
                    user={user}
                    messageSearch={messageSearch}
                    onMessageSearchChange={setMessageSearch}
                    panelTab={rightPanelTab}
                    onPanelTabChange={setRightPanelTab}
                    pinnedMessages={pinnedMessages}
                    onJumpToPinnedMessage={handleJumpToPinnedMessage}
                    onRemovePinnedMessage={handleRemovePinnedMessage}
                  />
                </div>
              ) : null}
            </div>

            <LayoutEditBar />

            <ComsCornerAd
              enabled={comsAdsEnabled}
              serverId={server ? server.id : null}
              serverName={server ? server.name : null}
              channelId={activeChannel ? activeChannel.id : null}
              tab={activeTab}
            />

            {createServerOpen ? (
              <CreateServerModal onClose={() => setCreateServerOpen(false)} onCreate={handleCreateServer} />
            ) : null}

            {createChannelOpen ? (
              <CreateChannelModal
                server={server}
                user={user}
                onClose={() => setCreateChannelOpen(false)}
                onCreate={(payload) => handleCreateChannel(payload)}
              />
            ) : null}
          </>
        );
      }}
    </ComsMobileShell>
    </>
  );
}